import { useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { AlertCircle, Bug } from "lucide-react";
import { usePageTitle } from "@/hooks/use-page-title";
import { useMe } from "@/hooks/queries/use-me";
import {
  useRoutingOptions,
  useStartInterview,
  type RoutingSelection,
} from "@/hooks/queries/use-fogbugz";
import { InterviewWizard } from "@/components/fogbugz/interview-wizard";
import { RoutingScreen } from "@/components/fogbugz/routing-screen";
import { PageInfoButton } from "@/components/common/page-info-button";
import { Button } from "@/components/ui/button";

/** Message text for a failure, without leaking a non-Error throw. */
function errorMessage(error: unknown, fallback: string): string {
  return error instanceof Error ? error.message : fallback;
}

export function FogBugzFormulatorPage() {
  usePageTitle("FogBugz Formulator");

  const { data: meData, isLoading: isLoadingMe } = useMe();
  const hasFogbugzKey = meData?.hasFogbugzKey ?? false;

  // The routing lists gate the whole screen, so they are only fetched once the
  // reporter is known to have a key — otherwise every mount buys a guaranteed
  // 400 on the way to the Settings redirect below.
  const routingOptionsQuery = useRoutingOptions({ enabled: hasFogbugzKey });
  const startInterviewMutation = useStartInterview();

  const [sessionId, setSessionId] = useState<string | null>(null);
  const [firstQuestion, setFirstQuestion] = useState<string | null>(null);

  // The interview is NOT auto-started. It begins only when the reporter submits
  // the routing screen, so a case can never be filed into the FogBugz key's
  // default project the way case #17054 was (E195 findings log items 1, 3, 6).
  function handleStart(routing: RoutingSelection) {
    startInterviewMutation.mutate(routing, {
      onSuccess: (response) => {
        setSessionId(response.data.sessionId);
        setFirstQuestion(response.data.firstQuestion);
      },
      // The failure is rendered inline by RoutingScreen so the reporter's
      // selections survive a retry.
    });
  }

  // Loading state
  if (isLoadingMe) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center space-y-3">
          <div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full mx-auto" />
          <p className="text-sm text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  // Redirect to settings if no FogBugz key configured
  if (!hasFogbugzKey) {
    return <Navigate to="/settings" replace />;
  }

  const header = (
    <div className="flex items-center gap-2 mb-6">
      <Bug className="h-5 w-5 text-primary" />
      <h1 className="text-xl font-semibold">FogBugz Formulator</h1>
      <PageInfoButton title="FogBugz Formulator" />
    </div>
  );

  // Interview under way — hand over to the wizard exactly as before.
  if (sessionId && firstQuestion) {
    return (
      <div className="max-w-4xl mx-auto pt-8 px-4 pb-8">
        {header}

        <div className="glass-card rounded-2xl border border-white/[0.08] p-6">
          <div className="mb-4">
            <p className="text-sm text-muted-foreground">
              Answer the questions below to create a well-formed FogBugz case. You can paste
              screenshots with{" "}
              <kbd className="px-1 py-0.5 bg-white/[0.08] rounded text-xs">Ctrl+V</kbd> or drag and
              drop them.
            </p>
          </div>

          <InterviewWizard sessionId={sessionId} firstQuestion={firstQuestion} />
        </div>
      </div>
    );
  }

  // Routing lists still loading.
  if (routingOptionsQuery.isPending) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center space-y-3">
          <div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full mx-auto" />
          <p className="text-sm text-muted-foreground">Loading FogBugz projects...</p>
        </div>
      </div>
    );
  }

  // Routing lists unavailable. Never fall through to a routing screen with empty
  // selects: an unreadable stored key is what produced finding #2, and empty
  // dropdowns would put the reporter back where a case is filed by default.
  if (routingOptionsQuery.isError) {
    return (
      <div className="max-w-3xl mx-auto pt-8 px-4">
        {header}

        <div
          role="alert"
          className="p-6 bg-red-500/10 border border-red-500/20 rounded-lg text-red-500 space-y-3"
        >
          <div className="flex items-center gap-2">
            <AlertCircle className="h-4 w-4 shrink-0" />
            <h2 className="font-semibold">Could not load your FogBugz projects</h2>
          </div>
          <p className="text-sm">
            {errorMessage(
              routingOptionsQuery.error,
              "FogBugz did not return the project, area, and milestone lists."
            )}
          </p>
          <p className="text-sm">
            Save or re-save your FogBugz API key in{" "}
            <Link to="/settings" className="underline underline-offset-4">
              Settings
            </Link>
            , then try again. Without these lists there is nowhere to file the case, so the
            interview cannot start.
          </p>
          <div className="flex gap-2">
            <Button
              variant="destructive"
              onClick={() => {
                void routingOptionsQuery.refetch();
              }}
            >
              Try Again
            </Button>
            <Button variant="outline" asChild>
              <Link to="/settings">Go to Settings</Link>
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Routing screen — the FogBugz New Case header, before the first question.
  return (
    <div className="max-w-4xl mx-auto pt-8 px-4 pb-8">
      {header}

      <RoutingScreen
        options={routingOptionsQuery.data.data}
        onStart={handleStart}
        isStarting={startInterviewMutation.isPending}
        startError={
          startInterviewMutation.isError
            ? errorMessage(startInterviewMutation.error, "Failed to start the interview.")
            : null
        }
      />
    </div>
  );
}
